import { api } from './client'

/**
 * Row shape returned by `FormsController.List` (`GET /api/forms`, `Ok(items)`) — a flat array,
 * NOT the `{total,page,pageSize,items}` paginated envelope the old dead scaffolding in
 * `settings.ts` wrongly assumed (that scaffolding was never imported by any page, so the bug was
 * never exercised — see TASK-0036). The backend query filters `Where(f => f.IsActive)` before
 * projecting, so every row `List` returns is guaranteed active; the projection itself does not
 * include `isActive`, so there is no way to tell active from inactive from this endpoint alone —
 * `FormsPage.tsx` renders a static "Attivo" badge on every row for that reason (accurate, not
 * fabricated, since the filter already guarantees it).
 */
export interface FormListItem {
  id: number
  name: string
  slug: string
  description: string | null
  departmentId: number | null
  sortOrder: number
  createdAt: string
}

/**
 * Full entity as returned by `FormsController.Get` (`GET /api/forms/{id}`, `Ok(form)` — the whole
 * `Form` aggregate, not a projection). `fields` is a free-form JSON string on the domain side
 * (`Form.Fields`, nullable `string?`) — treated as opaque text here (a plain textarea in
 * `FormDetailPage.tsx`), not parsed or validated. A real structured field editor is a distinct
 * "Form Builder" feature, out of scope for this task.
 */
export interface FormDetail {
  id: number
  name: string
  slug: string
  description: string | null
  fields: string | null
  departmentId: number | null
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string | null
}

/**
 * Body accepted by `Create`/`Update` (`FormsController.FormRequest`). Note two backend quirks
 * discovered while building this client (left untouched — out of `allowed_paths` for this task,
 * see TASK-0036) that shape how `FormDetailPage.tsx` uses this type:
 *  - `description` is never persisted by either action (`Form.Create` doesn't accept it, and
 *    `Form.UpdateFields` only touches `Fields`) — the domain `Form.Description` property has no
 *    public setter at all, so it is always `null` no matter what a caller sends.
 *  - `Update` (`PUT /api/forms/{id}`) only calls `form.UpdateFields(req.Fields)` — `name`/`slug`/
 *    `departmentId` sent in the body are silently ignored for persistence. They're still required
 *    (non-nullable) on this DTO, so `FormDetailPage.tsx` echoes back the existing values on update
 *    to satisfy `[ApiController]` model binding, but only exposes `fields` as actually editable
 *    once a form exists (name/slug/department are only meaningfully set at `Create` time).
 */
export interface FormRequest {
  name: string
  slug: string
  description?: string | null
  departmentId?: number | null
  fields?: string | null
}

export const formsApi = {
  list: (departmentId?: number) => {
    const q = new URLSearchParams()
    if (departmentId != null) q.set('departmentId', String(departmentId))
    const qs = q.toString()
    return api<FormListItem[]>(`/api/forms${qs ? `?${qs}` : ''}`)
  },
  get: (id: number) => api<FormDetail>(`/api/forms/${id}`),
  /** `[Authorize(PolicyNames.EqualStaff)]` + server-side `CanManageForms` category-permission
   * gate (Admin bypasses) — the frontend cannot know this permission in advance the way it can
   * know Admin-or-not from the role claim, so callers must catch and surface a 403 rather than
   * pre-hiding the action. */
  create: (data: FormRequest) =>
    api<{ id: number }>('/api/forms', { method: 'POST', body: JSON.stringify(data) }),
  /** Same `CanManageForms` gate as `create`. See the `FormRequest` doc comment: only `fields` is
   * actually persisted by this endpoint. */
  update: (id: number, data: FormRequest) =>
    api<void>(`/api/forms/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  /** Admin-only on the backend (`[Authorize(PolicyNames.Admin)]`) — callers must check
   * `auth.role` before offering this in the UI (same pattern as `companiesApi.remove`). */
  remove: (id: number) => api<void>(`/api/forms/${id}`, { method: 'DELETE' }),
}
